// THE ONE HOURS CALCULATOR (round 44).
//
// Every number of hours Dayboard shows, prints or exports comes from here: the
// sheet's live totals on a phone, the board, the PDF, both CSVs and Home. No
// total is ever stored, so a correction can never leave a stale one behind.
//
// THIS FILE IS COPIED BYTE FOR BYTE to web/src/lib/timesheetHours.ts, so the
// total a worker sees while typing is the total the office approves. A test
// (server/test/timesheet-hours.test.ts) fails if the two copies differ. Edit
// one, copy it over the other. It imports nothing, which is what lets it live
// on both sides of the wire.
//
// HOURS ONLY. Nothing here knows about pay, ordinary time, overtime bands or
// awards (Nathan, 2026-10-06). Worked minutes are the window less the unpaid
// break; leave is what the day says it is. The bookkeeper does the rest.
//
// WHOLE MINUTES, integers throughout. Decimal hours appear only at the very
// edge, as a string for the CSV, so no float ever gets added to another.
//
// A DAY MAY FINISH AFTER MIDNIGHT. A finish earlier than the start is the next
// morning (19:00 to 05:00 is ten hours), and the hours belong to the day the
// shift STARTED. A finish EQUAL to the start is refused by the database, so it
// never reaches this file as a 24-hour shift.

export interface DayHours {
  /** 'HH:MM' or 'HH:MM:SS'; both null, or both set. */
  startTime: string | null;
  finishTime: string | null;
  breakMinutes: number;
  leaveTypeId: string | null;
  /** Null with a leave type means the whole day. */
  leaveMinutes: number | null;
}

export interface WeekTotals {
  workedMinutes: number;
  breakMinutes: number;
  /** Leave stated in hours. Whole days of leave are counted separately. */
  leaveMinutes: number;
  /** Days marked as leave with no hours stated. */
  fullLeaveDays: number;
  /** Days with any worked time. */
  daysWorked: number;
}

const MINUTES_PER_DAY = 1440;

/** '07:30' or '07:30:00' to minutes after midnight. */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/** True when the finish is the next morning. */
export function finishesNextDay(day: Pick<DayHours, 'startTime' | 'finishTime'>): boolean {
  if (day.startTime === null || day.finishTime === null) return false;
  return timeToMinutes(day.finishTime) < timeToMinutes(day.startTime);
}

/** Start to finish, wrapping past midnight. Zero when there is no window. */
export function spanMinutes(day: Pick<DayHours, 'startTime' | 'finishTime'>): number {
  if (day.startTime === null || day.finishTime === null) return 0;
  const span = timeToMinutes(day.finishTime) - timeToMinutes(day.startTime);
  return span > 0 ? span : span + MINUTES_PER_DAY;
}

/** The window less the unpaid break. Never negative. */
export function dayWorkedMinutes(day: DayHours): number {
  const span = spanMinutes(day);
  if (span === 0) return 0;
  return Math.max(0, span - day.breakMinutes);
}

/** A day of leave with no hours stated. */
export function isFullDayLeave(day: DayHours): boolean {
  return day.leaveTypeId !== null && day.leaveMinutes === null && day.startTime === null;
}

/** Leave stated in minutes; zero for no leave and for a full day. */
export function dayLeaveMinutes(day: DayHours): number {
  return day.leaveTypeId !== null && day.leaveMinutes !== null ? day.leaveMinutes : 0;
}

/** Whether the day says anything at all: hours or leave. */
export function dayHasEntry(day: DayHours): boolean {
  return day.startTime !== null || day.leaveTypeId !== null;
}

export function weekTotals(days: readonly DayHours[]): WeekTotals {
  const totals: WeekTotals = {
    workedMinutes: 0,
    breakMinutes: 0,
    leaveMinutes: 0,
    fullLeaveDays: 0,
    daysWorked: 0,
  };
  for (const day of days) {
    const worked = dayWorkedMinutes(day);
    totals.workedMinutes += worked;
    if (worked > 0) {
      totals.daysWorked += 1;
      totals.breakMinutes += day.breakMinutes;
    }
    totals.leaveMinutes += dayLeaveMinutes(day);
    if (isFullDayLeave(day)) totals.fullLeaveDays += 1;
  }
  return totals;
}

/** 450 to '7:30'. For screens and the PDF. */
export function minutesToHm(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${hours}:${rest < 10 ? '0' : ''}${rest}`;
}

/**
 * 450 to '7.50'. For the CSV only, where a bookkeeper's spreadsheet wants a
 * number. Exact to the cent of an hour by integer arithmetic: a minute is
 * 1.666... hundredths, so round once, at the end.
 */
export function minutesToDecimalHours(minutes: number): string {
  const hundredths = Math.round((Math.max(0, minutes) * 100) / 60);
  const hours = Math.floor(hundredths / 100);
  const rest = hundredths % 100;
  return `${hours}.${rest < 10 ? '0' : ''}${rest}`;
}
